import { ScrollArea } from '@radix-ui/themes';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertPopover from 'components/HubertUI/HubertPopover';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import HubertSelect from 'components/HubertUI/HubertSelect';
import MutedText from 'components/common/MutedText';
import { MessagesSquare } from 'lucide-react';
import { type ComponentProps } from 'react';
import { ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context';
import Reply from './Reply';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';

import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { toast } from 'sonner';
import { addReply, addReplyOptions, editReply } from 'rtk/Question.Editor.Slice';
import { updateInTemplateReplyOptions } from 'rtk/questionEditorThunks';
import { useTemplateId } from 'hooks/useTemplate';

interface Props extends ComponentProps<typeof HubertPopover> {
  replies: string[];
  field: 'options' | 'intents';
  fieldIndex: number;
  optionId?: string;
}

const ReplyPopover = ({ replies, field, fieldIndex, optionId, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  const mainContent = document.getElementById('main-content') as HTMLElement;
  const dispatch = useAppDispatch();
  const templateId = useTemplateId();
  const errors = useAppSelector((state) => state.questionEditor.byId[questionId].optionsErrors);
  if (errors?.length) errors.map((x) => toast.error(x));
  return (
    <HubertPopover {...props}>
      <HubertTooltip>
        <HubertTooltip.Trigger asChild>
          <HubertPopover.Trigger asChild>
            <HubertButton
              variant="secondary"
              className="cursor-pointer"
              icon={<MessagesSquare className={replies.length ? 'text-primary-500' : 'text-gray-500'} />}
            ></HubertButton>
          </HubertPopover.Trigger>
        </HubertTooltip.Trigger>
        <HubertTooltip.Content>{t('replyPopover.tooltip')}</HubertTooltip.Content>
      </HubertTooltip>
      <HubertPopover.Content
        className={clsx('flex flex-col justify-between', 'min-h-[100px]', 'max-w-[500px] min-w-[500px]', 'p-[18px]')}
        portalContent={true}
        portalElement={mainContent}
      >
        <div className={clsx('mr-4 flex flex-row justify-between', 'pb-2')}>
          <span>
            {t('replyPopover.heading')}
            {/* TODO(i18n): Category 4 — dynamic field name substring embedded mid-sentence; cannot extract independently */}
            <MutedText className="pl-2 text-sm font-normal text-gray-500">
              This is what Hubert replies with on this {field.substring(0, field.length - 1)}.
            </MutedText>
          </span>

          <HubertButton
            size="xs"
            disabled={field === 'intents' && inTemplateEditAndUneditable}
            onClick={() => {
              if (field === 'intents') dispatch(addReply({ questionId, fieldIndex, language: questionLanguage }));
              else if (inTemplateEditAndUneditable) {
                updateInTemplateReplyOptions({
                  templateId,
                  questionId,
                  optionId,
                  replies,
                  operation: 'add',
                  language: questionLanguage,
                  dispatch,
                  mode,
                });
              } else {
                dispatch(addReplyOptions({ questionId, fieldIndex, language: questionLanguage }));
              }
            }}
          >
            {t('replyPopover.addReply')}
          </HubertButton>
        </div>

        <ScrollArea className={clsx('max-h-[200px] min-h-[200px]', 'my-4')}>
          {replies.slice(0, field === 'intents' ? replies.length - 1 : replies.length).map((reply, index) => {
            return (
              <Reply
                key={`intent-reply-${index}`}
                reply={reply}
                replies={replies}
                field={field}
                fieldIndex={fieldIndex}
                itemIndex={index}
                optionId={optionId}
              />
            );
          })}
        </ScrollArea>

        {field === 'intents' ? (
          <div className={clsx('flex flex-row justify-end', 'pt-6')}>
            <HubertSelect
              valuePlaceholder={t('replyPopover.actionPlaceholder')}
              disabled={mode === ECreateQuestionMode.InTemplateEdit}
              onValueChange={(e) => {
                dispatch(
                  editReply({
                    questionId,
                    fieldIndex,
                    itemIndex: replies.length - 1,
                    newReply: e,
                    language: questionLanguage,
                  }),
                );
              }}
              value={replies[replies.length - 1]}
              list={[
                { label: t('replyPopover.actions.stopSleep'), value: 'sleep' },
                { label: t('replyPopover.actions.nextContinue'), value: 'next' },
              ]}
            />
          </div>
        ) : null}
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default ReplyPopover;
